import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
    Plus,
    Edit01,
    SearchLg,
    ArrowRight,
    Trash01,
    ChevronRight
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { useTranslation } from 'react-i18next';
import { useDocumentTypes, useCreateDocumentType, useUpdateDocumentType, useDeleteDocumentType } from '@/hooks/api/use-document-types';
import { useDocumentSchemas } from '@/hooks/api/use-document-schemas';
import { useToast } from '@/hooks/use-toast';
import type { SortDescriptor } from 'react-aria-components';

interface DocumentTypeWithStats {
    document_type_id: string;
    key: string;
    name: string;
    description: string | null;
    phase: string | null;
    category: string | null;
    classifier_keys: string[] | null;
    schemasCount: number;
    attributesCount: number;
    created_at: string | null;
}

type GroupByOption = 'none' | 'phase' | 'category';

export const DocumentTypesPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const toast = useToast();
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDocType, setEditingDocType] = useState<DocumentTypeWithStats | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [docTypeToDelete, setDocTypeToDelete] = useState<DocumentTypeWithStats | null>(null);
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: 'attributesCount',
        direction: 'descending',
    });
    const [groupBy, setGroupBy] = useState<GroupByOption>('none');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    const [formData, setFormData] = useState({
        key: '',
        name: '',
        description: '',
        phase: '',
        category: '',
        classifier_keys: '',
    });

    // Fetch document types from API
    const { data: documentTypes = [], isLoading: isLoadingTypes, isError: isErrorTypes } = useDocumentTypes({
        limit: 500, // Get all document types
    });

    // Fetch all schemas to calculate statistics
    // Only fetch if document types are loaded and available
    const { data: allSchemas = [], isLoading: isLoadingSchemas } = useDocumentSchemas(
        {
            limit: 500, // Get all schemas (max 500)
        },
        {
            enabled: documentTypes.length > 0 && !isLoadingTypes,
        }
    );

    // Calculate statistics for each document type using denormalized attribute_count from schemas
    const documentTypesWithStats = useMemo<DocumentTypeWithStats[]>(() => {
        if (!documentTypes.length) {
            return [];
        }

        return documentTypes.map((docType) => {
            const schemasForType = allSchemas.filter(
                (schema) => schema.document_type === docType.key
            );

            const attributesCount = schemasForType.reduce(
                (max, schema) => Math.max(max, schema.attribute_count || 0),
                0
            );

            return {
                document_type_id: docType.document_type_id,
                key: docType.key,
                name: docType.name,
                description: docType.description,
                phase: docType.phase ?? null,
                category: docType.category ?? null,
                classifier_keys: docType.classifier_keys ?? null,
                schemasCount: schemasForType.length,
                attributesCount,
                created_at: docType.created_at ?? null,
            };
        });
    }, [documentTypes, allSchemas]);

    // Filter document types by search term
    const filteredBySearch = useMemo(() => {
        if (!searchTerm) return documentTypesWithStats;

        const term = searchTerm.toLowerCase();
        return documentTypesWithStats.filter(
            (dt) =>
                dt.key.toLowerCase().includes(term) ||
                dt.name.toLowerCase().includes(term) ||
                (dt.description && dt.description.toLowerCase().includes(term))
        );
    }, [documentTypesWithStats, searchTerm]);

    // Sort the filtered results
    const sortedDocumentTypes = useMemo(() => {
        if (!sortDescriptor.column) return filteredBySearch;

        return [...filteredBySearch].sort((a, b) => {
            const column = sortDescriptor.column as keyof DocumentTypeWithStats;
            let aValue = a[column];
            let bValue = b[column];

            // Handle null/undefined values
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';

            // Compare values
            let comparison = 0;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                comparison = aValue - bValue;
            } else {
                comparison = String(aValue).localeCompare(String(bValue));
            }

            return sortDescriptor.direction === 'descending' ? -comparison : comparison;
        });
    }, [filteredBySearch, sortDescriptor]);

    // Group document types by phase or category
    const groupedDocumentTypes = useMemo(() => {
        if (groupBy === 'none') return null;

        const groups = new Map<string, DocumentTypeWithStats[]>();
        const ungroupedKey = t('types.filters.ungrouped', { defaultValue: 'Ungrouped' });

        sortedDocumentTypes.forEach((dt) => {
            const groupValue = groupBy === 'phase' ? dt.phase : dt.category;
            const key = groupValue || ungroupedKey;

            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key)!.push(dt);
        });

        // Sort groups by count (most items first), but put "Ungrouped" at the end
        return Array.from(groups.entries()).sort((a, b) => {
            if (a[0] === ungroupedKey) return 1;
            if (b[0] === ungroupedKey) return -1;
            return b[1].length - a[1].length; // Descending by count
        });
    }, [sortedDocumentTypes, groupBy, t]);

    const isLoading = isLoadingTypes || isLoadingSchemas;
    const isError = isErrorTypes;

    // Mutations
    const createDocumentType = useCreateDocumentType();
    const updateDocumentType = useUpdateDocumentType();
    const deleteDocumentType = useDeleteDocumentType();

    const handleOpenModal = (docType?: DocumentTypeWithStats) => {
        if (docType) {
            setEditingDocType(docType);
            setFormData({
                key: docType.key,
                name: docType.name,
                description: docType.description || '',
                phase: docType.phase || '',
                category: docType.category || '',
                classifier_keys: docType.classifier_keys?.join(', ') || '',
            });
        } else {
            setEditingDocType(null);
            setFormData({
                key: '',
                name: '',
                description: '',
                phase: '',
                category: '',
                classifier_keys: '',
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingDocType(null);
        setFormData({
            key: '',
            name: '',
            description: '',
            phase: '',
            category: '',
            classifier_keys: '',
        });
    };

    const handleSave = async () => {
        if (!formData.key || !formData.name) {
            toast.error(t('types.modal.validationError', { defaultValue: 'Key and name are required' }));
            return;
        }

        try {
            const classifierKeys = formData.classifier_keys
                ? formData.classifier_keys.split(',').map(k => k.trim()).filter(Boolean)
                : [];

            if (editingDocType) {
                // Update existing
                await updateDocumentType.mutateAsync({
                    documentTypeId: editingDocType.document_type_id,
                    data: {
                        name: formData.name,
                        description: formData.description || null,
                        phase: formData.phase || null,
                        category: formData.category || null,
                        classifier_keys: classifierKeys,
                    },
                });
            } else {
                // Create new
                await createDocumentType.mutateAsync({
                    key: formData.key,
                    name: formData.name,
                    description: formData.description || null,
                    phase: formData.phase || null,
                    category: formData.category || null,
                    classifier_keys: classifierKeys,
                });
            }
            handleCloseModal();
        } catch (error) {
            console.error('Error saving document type:', error);
            toast.error(
                t('types.modal.saveError', {
                    defaultValue: 'Error saving document type. Please try again.',
                })
            );
        }
    };

    const handleDeleteClick = (docType: DocumentTypeWithStats) => {
        setDocTypeToDelete(docType);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!docTypeToDelete) return;

        try {
            await deleteDocumentType.mutateAsync(docTypeToDelete.document_type_id);
            setIsDeleteModalOpen(false);
            setDocTypeToDelete(null);
        } catch (error) {
            console.error('Error deleting document type:', error);
            toast.error(
                t('types.modal.deleteError', {
                    defaultValue: 'Error deleting document type. Please try again.',
                })
            );
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setDocTypeToDelete(null);
    };

    const toggleGroup = (groupName: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupName)) {
                next.delete(groupName);
            } else {
                next.add(groupName);
            }
            return next;
        });
    };

    const toggleAllGroups = () => {
        if (!groupedDocumentTypes) return;
        const allGroupNames = groupedDocumentTypes.map(([name]) => name);
        const allExpanded = allGroupNames.every(name => expandedGroups.has(name));

        if (allExpanded) {
            setExpandedGroups(new Set());
        } else {
            setExpandedGroups(new Set(allGroupNames));
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading', { defaultValue: 'Loading...' })}</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-error mb-4">
                        {t('types.error.loading', { defaultValue: 'Error loading document types' })}
                    </p>
                    <Button onClick={() => window.location.reload()} size="md" color="primary">
                        {t('common.retry', { defaultValue: 'Retry' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('types.title')}</h2>
                    <p className="text-tertiary mt-1">{t('types.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('types.newDocumentType')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('types.filters.keywordPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-tertiary whitespace-nowrap">{t('types.filters.groupBy')}:</span>
                        <div className="flex rounded-lg ring-1 ring-secondary overflow-hidden">
                            <button
                                onClick={() => setGroupBy('none')}
                                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                    groupBy === 'none'
                                        ? 'bg-brand text-white'
                                        : 'bg-primary text-secondary hover:bg-secondary'
                                }`}
                            >
                                {t('types.filters.groupByNone')}
                            </button>
                            <button
                                onClick={() => setGroupBy('phase')}
                                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-secondary ${
                                    groupBy === 'phase'
                                        ? 'bg-brand text-white'
                                        : 'bg-primary text-secondary hover:bg-secondary'
                                }`}
                            >
                                {t('types.filters.groupByPhase')}
                            </button>
                            <button
                                onClick={() => setGroupBy('category')}
                                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-secondary ${
                                    groupBy === 'category'
                                        ? 'bg-brand text-white'
                                        : 'bg-primary text-secondary hover:bg-secondary'
                                }`}
                            >
                                {t('types.filters.groupByCategory')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Types - Table or Grouped View */}
            {groupBy === 'none' ? (
                <TableCard.Root>
                    <TableCard.Header
                        title={`${t('types.title')} (${sortedDocumentTypes.length})`}
                        description={t('types.description')}
                    />

                    <Table sortDescriptor={sortDescriptor} onSortChange={setSortDescriptor}>
                        <Table.Header>
                            <Table.Head id="name" label={t('types.headers.label')} isRowHeader allowsSorting />
                            <Table.Head id="phase" label={t('types.headers.phase')} allowsSorting />
                            <Table.Head id="category" label={t('types.headers.category')} allowsSorting />
                            <Table.Head id="schemasCount" label={t('types.headers.schemasCount')} allowsSorting />
                            <Table.Head id="attributesCount" label={t('types.headers.attributesCount')} allowsSorting />
                            <Table.Head label={t('types.headers.actions')} />
                        </Table.Header>
                        <Table.Body>
                            {sortedDocumentTypes.map((docType) => (
                                <Table.Row key={docType.document_type_id}>
                                    <Table.Cell>
                                        <div>
                                            <span className="text-sm font-medium text-primary">{docType.name}</span>
                                            {docType.classifier_keys && docType.classifier_keys.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {docType.classifier_keys.map((k) => (
                                                        <span key={k} className="px-1.5 py-0.5 text-[10px] rounded bg-tertiary text-secondary">
                                                            {k}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">{docType.phase || '-'}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">{docType.category || '-'}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-primary">{docType.schemasCount}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-primary">{docType.attributesCount}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/admin/documents/types/${docType.document_type_id}/schemas`}
                                                className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                            >
                                                {t('types.actions.viewSchemas')}
                                                <ArrowRight className="size-4" />
                                            </Link>
                                            <Button
                                                onClick={() => handleOpenModal(docType)}
                                                size="sm"
                                                color="secondary"
                                                iconLeading={Edit01}
                                            >
                                                {t('types.actions.edit')}
                                            </Button>
                                            <Button
                                                onClick={() => handleDeleteClick(docType)}
                                                size="sm"
                                                color="primary-destructive"
                                                iconLeading={Trash01}
                                            >
                                                {t('types.actions.delete')}
                                            </Button>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableCard.Root>
            ) : (
                <div className="space-y-4">
                    {/* Expand/Collapse All Button */}
                    <div className="flex justify-end">
                        <Button
                            onClick={toggleAllGroups}
                            size="sm"
                            color="secondary"
                        >
                            {groupedDocumentTypes && groupedDocumentTypes.every(([name]) => expandedGroups.has(name))
                                ? t('types.filters.collapseAll', { defaultValue: 'Collapse All' })
                                : t('types.filters.expandAll', { defaultValue: 'Expand All' })}
                        </Button>
                    </div>

                    {groupedDocumentTypes?.map(([groupName, items]) => {
                        const isExpanded = expandedGroups.has(groupName);
                        return (
                            <div key={groupName} className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden">
                                {/* Accordion Header */}
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(groupName)}
                                    className="w-full px-6 py-4 flex items-center gap-3 hover:bg-secondary transition-colors"
                                    aria-expanded={isExpanded}
                                >
                                    <ChevronRight
                                        className={`size-5 text-fg-quaternary transition-transform duration-200 ${
                                            isExpanded ? 'rotate-90' : ''
                                        }`}
                                    />
                                    <h3 className="text-lg font-semibold text-primary flex-1 text-left">
                                        {groupName}
                                        <span className="ml-2 text-sm font-normal text-tertiary">({items.length})</span>
                                    </h3>
                                </button>

                                {/* Accordion Content */}
                                {isExpanded && (
                                    <div className="border-t border-secondary divide-y divide-secondary">
                                        {items.map((docType) => (
                                            <div key={docType.document_type_id} className="px-6 py-4 flex items-center justify-between hover:bg-secondary transition-colors">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-sm font-medium text-primary">{docType.name}</span>
                                                        {groupBy === 'phase' && docType.category && (
                                                            <span className="px-2 py-0.5 text-xs rounded-full bg-tertiary text-primary">
                                                                {docType.category}
                                                            </span>
                                                        )}
                                                        {groupBy === 'category' && docType.phase && (
                                                            <span className="px-2 py-0.5 text-xs rounded-full bg-tertiary text-primary">
                                                                {docType.phase}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {docType.description && (
                                                        <p className="text-sm text-tertiary mt-1 truncate">{docType.description}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-4 ml-4">
                                                    <div className="text-sm text-tertiary">
                                                        <span className="font-medium text-primary">{docType.schemasCount}</span> {t('types.headers.schemasCount').toLowerCase()}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Link
                                                            to={`/admin/documents/types/${docType.document_type_id}/schemas`}
                                                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                                        >
                                                            {t('types.actions.viewSchemas')}
                                                            <ArrowRight className="size-4" />
                                                        </Link>
                                                        <Button
                                                            onClick={() => handleOpenModal(docType)}
                                                            size="sm"
                                                            color="secondary"
                                                            iconLeading={Edit01}
                                                        >
                                                            {t('types.actions.edit')}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {sortedDocumentTypes.length === 0 && !isLoading && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">
                            {t('noData.title', { defaultValue: 'No data found' })}
                        </h3>
                        <p className="text-tertiary">
                            {t('noData.message', { defaultValue: 'No document types match your filters.' })}
                        </p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingDocType ? t('types.modal.edit') : t('types.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <Input
                                        label={t('types.modal.fields.key')}
                                        placeholder={t('types.modal.fields.keyPlaceholder')}
                                        value={formData.key}
                                        onChange={(value) => setFormData({ ...formData, key: value })}
                                        isRequired
                                        size="md"
                                        isDisabled={!!editingDocType}
                                    />

                                    <Input
                                        label={t('types.modal.fields.label')}
                                        placeholder={t('types.modal.fields.labelPlaceholder')}
                                        value={formData.name}
                                        onChange={(value) => setFormData({ ...formData, name: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('types.modal.fields.description')}
                                        placeholder={t('types.modal.fields.descriptionPlaceholder')}
                                        value={formData.description}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-2 gap-4">
                                        <Input
                                            label={t('types.modal.fields.phase')}
                                            placeholder={t('types.modal.fields.phasePlaceholder')}
                                            value={formData.phase}
                                            onChange={(value) => setFormData({ ...formData, phase: value })}
                                            size="md"
                                        />

                                        <Input
                                            label={t('types.modal.fields.category')}
                                            placeholder={t('types.modal.fields.categoryPlaceholder')}
                                            value={formData.category}
                                            onChange={(value) => setFormData({ ...formData, category: value })}
                                            size="md"
                                        />
                                    </div>

                                    <div>
                                        <Input
                                            label="Classifier Keys"
                                            placeholder="Comma-separated keys (e.g. sad, bill_of_lading, pod)"
                                            value={formData.classifier_keys}
                                            onChange={(value) => setFormData({ ...formData, classifier_keys: value })}
                                            size="md"
                                        />
                                        <p className="text-xs text-tertiary mt-1">
                                            Alternative keys the document classifier may output for this type
                                        </p>
                                    </div>
                                </div>

                                {(createDocumentType.isPending || updateDocumentType.isPending) && (
                                    <div className="mt-4 text-sm text-tertiary">
                                        {t('types.modal.saving', { defaultValue: 'Saving...' })}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={handleCloseModal}
                                        size="md"
                                        color="secondary"
                                        isDisabled={createDocumentType.isPending || updateDocumentType.isPending}
                                    >
                                        {t('modal.cancel', { defaultValue: 'Cancel' })}
                                    </Button>
                                    <Button
                                        onClick={handleSave}
                                        size="md"
                                        color="primary"
                                        isDisabled={createDocumentType.isPending || updateDocumentType.isPending}
                                    >
                                        {editingDocType
                                            ? t('modal.save', { defaultValue: 'Save' })
                                            : t('modal.create', { defaultValue: 'Create' })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={handleDeleteCancel}
                                        size="md"
                                        color="secondary"
                                        isDisabled={deleteDocumentType.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleDeleteConfirm}
                                        size="md"
                                        color="primary-destructive"
                                        isDisabled={deleteDocumentType.isPending}
                                    >
                                        {t('modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
